/** Color-only question model. Core owns what is still unanswered; this module
 * gives those facts a human surface and never imports Type Set concerns. */

import { copy, pendingQuestionIds, type SharedBrief } from "@core/index.js";

import { colourQuestion } from "./catalog";
import type { QuestionOption } from "./types";

export type StudioQuestionKind = "single" | "multi" | "text" | "number";

export type StudioQuestion = {
  id: string;
  kind: StudioQuestionKind;
  title: string;
  note: string;
  options: QuestionOption[];
  min?: number;
  max?: number;
};

const t = copy.questions;

const FRIENDLY_LABELS: Record<string, Record<string, string>> = {
  sourceRelationship: {
    reference_is_identity: "Stay close—this image is the world",
    protect_one: "Keep one important color sacred",
    starting_point: "Borrow the mood; let the colors move",
    surprise_me_carefully: "Surprise me, but make it make sense",
    unknown: "Use your best judgment",
  },
  baseMode: {
    light: "Mostly light slides",
    dark: "Mostly dark slides",
    both: "Give me light and dark versions",
    decide: "Let the image decide",
  },
};

const LOCAL_OPTIONS: Record<string, QuestionOption[]> = {
  viewingContexts: [
    { id: "projector", label: "Large room or projector" },
    { id: "laptop", label: "Laptop or screen-share" },
    { id: "phone", label: "Phone often" },
    { id: "mixedUnknown", label: "Mixed or unknown" },
  ],
  density: [
    { id: "sparse", label: "Sparse—title, statement, image" },
    { id: "moderate", label: "Moderate—title plus short explanation" },
    { id: "dense", label: "Dense—paragraphs, bios, detail, appendix" },
    { id: "varied", label: "It varies a lot" },
  ],
};

const DESCRIPTORS: Record<string, Omit<StudioQuestion, "options">> = {
  viewingContexts: { id: "viewingContexts", kind: "multi", title: t.viewing.title, note: t.viewing.note },
  density: { id: "density", kind: "single", title: t.pressure.title, note: t.pressure.note },
  sourceRelationship: { id: "sourceRelationship", kind: "single", title: t.sourceRelationship.title, note: t.sourceRelationship.note },
  baseMode: { id: "baseMode", kind: "single", title: t.baseMode.title, note: t.baseMode.note },
  dataNeed: {
    id: "dataNeed",
    kind: "single",
    title: "Will color need to carry charts or comparisons?",
    note: "If a chart compares things, we will reserve colors that stay distinct. Labels still do the explaining.",
  },
  dataCount: {
    id: "dataCount",
    kind: "number",
    title: "How many series or steps?",
    note: "Pick 2–12. Specific beats ‘quite a few,’ which has never rescued a chart.",
    min: 2,
    max: 12,
  },
  divergingMidpoint: {
    id: "divergingMidpoint",
    kind: "text",
    title: "What does the middle mean?",
    note: "Choose one: zero, parity, target, break-even—or write the real threshold.",
  },
};

const ORDER = ["viewingContexts", "density", "sourceRelationship", "baseMode", "dataNeed", "dataCount", "divergingMidpoint"];

const optionsFor = (id: string): QuestionOption[] => {
  const local = LOCAL_OPTIONS[id];
  if (local) return local;
  const question = colourQuestion(id as never);
  if (!question) return [];
  return question.options.map((optionId) => ({
    id: optionId,
    label: FRIENDLY_LABELS[id]?.[optionId] ?? question.labels[optionId] ?? optionId,
  }));
};

export const studioQuestion = (id: string): StudioQuestion | null => {
  const descriptor = DESCRIPTORS[id];
  return descriptor ? { ...descriptor, options: optionsFor(id) } : null;
};

export const questionQueue = (brief: SharedBrief): StudioQuestion[] => {
  const pending = new Set(pendingQuestionIds(brief));
  return ORDER.filter((id) => pending.has(id))
    .map((id) => studioQuestion(id))
    .filter((question): question is StudioQuestion => question !== null);
};

export const answerBrief = (brief: SharedBrief, id: string, value: unknown): SharedBrief => {
  switch (id) {
    case "viewingContexts":
      return { ...brief, viewingContexts: value as SharedBrief["viewingContexts"] };
    case "density":
      return { ...brief, density: value as SharedBrief["density"] };
    case "sourceRelationship":
      return { ...brief, sourceRelationship: value as SharedBrief["sourceRelationship"] };
    case "baseMode":
      return { ...brief, baseMode: value as SharedBrief["baseMode"] };
    case "dataNeed":
      return {
        ...brief,
        dataNeed: value as SharedBrief["dataNeed"],
        ...(["categorical", "sequential", "diverging"].includes(String(value)) ? {} : { dataCount: undefined, divergingMidpoint: undefined }),
        ...(value === "diverging" ? {} : { divergingMidpoint: undefined }),
      };
    case "dataCount":
      return { ...brief, dataCount: value as number };
    case "divergingMidpoint":
      return { ...brief, divergingMidpoint: value as string };
    default:
      return brief;
  }
};

export const briefValue = (brief: SharedBrief, id: string): unknown =>
  (brief as unknown as Record<string, unknown>)[id];
